import Close from '@mui/icons-material/Close'
import { Alert, Box, Grid, Snackbar, Stack, Typography } from '@mui/material'
import { useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FormDefinition } from '../../types/apiModels'
import DynamicFieldSwitcher from './DynamicFieldSwitcher'
import { DynamicFormYearsContext } from './dynamicFormYearsContext'
import { checkDependencies, parseErrors } from './utils'

interface FormData {
  notApplicable?: boolean
  [key: string]: any
}

interface DynamicFormProps {
  readOnly?: boolean
  year?: number
  formDefinition: FormDefinition
  saveFormData?: (data: FormData) => void
  handleClose?: () => void
  data?: FormData
  children?: React.ReactNode
  onSubmit?: (data: FormData) => void
}

const EMPTY_DATA: FormData = {}

const DynamicForm = ({
  readOnly = false,
  year,
  formDefinition,
  saveFormData,
  handleClose,
  data = EMPTY_DATA,
  children,
  onSubmit,
}: DynamicFormProps) => {
  const formDefinitionDefaultValues = useMemo(() => {
    return formDefinition.fields.reduce((acc, field) => {
      if (field.defaultValue) {
        acc[field.name] = field.defaultValue
      }
      return acc
    }, {})
  }, [formDefinition])

  const form = useForm<FormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    shouldFocusError: true,
    shouldUnregister: true,
    shouldUseNativeValidation: false,
    defaultValues: { notApplicable: false, ...formDefinitionDefaultValues, ...data },
  })

  const {
    handleSubmit,
    formState: { errors },
    watch,
    clearErrors,
  } = form

  const handleFormSubmit = (formData: FormData) => {
    if (onSubmit) {
      onSubmit(formData)
    } else if (saveFormData) {
      saveFormData(formData)
    }
  }

  const dynamicFormYearsContextValue = useMemo(
    () => ({ year: year ?? null, lastFarmSubdivisionYear: null }),
    [year],
  )

  return (
    <Grid container>
      <Grid container alignItems="center" sx={{ width: '100%' }}>
        {formDefinition?.label && (
          <Grid>
            <Typography
              sx={{
                fontSize: 20,
                fontWeight: 600,
                textAlign: 'left',
              }}
            >
              {formDefinition.label}
            </Typography>
          </Grid>
        )}

        {handleClose && (
          <Grid
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              flexGrow: 1,
            }}
          >
            <Close
              sx={{
                margin: 1,
                cursor: 'pointer',
                '&:hover': {
                  color: 'red',
                },
              }}
              onClick={handleClose}
            />
          </Grid>
        )}
      </Grid>

      <Grid sx={{ maxHeight: '80vh', width: '100%', overflowY: 'scroll' }}>
        <FormProvider {...form}>
          <DynamicFormYearsContext.Provider value={dynamicFormYearsContextValue}>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
              <Box pb={3} pl={0} pr={0} pt={1}>
                {!watch('notApplicable') && (
                  <fieldset style={{ color: 'inherit' }}>
                    <Grid container spacing={1}>
                      {formDefinition.fields.map(formField => (
                        <DynamicFieldSwitcher
                          key={`${formField.name}`}
                          checkDependencies={checkDependencies}
                          fieldArrayIndex={undefined}
                          fieldArrayName={undefined}
                          formField={formField}
                          index={undefined}
                          readOnly={readOnly}
                        />
                      ))}
                    </Grid>
                  </fieldset>
                )}
              </Box>
              <Stack direction="column" display="flex" position="sticky" spacing={1}>
                {errors && Object.keys(errors).length > 0 && (
                  <Snackbar key="error-snackbar" open={!!errors} onClose={() => clearErrors()}>
                    <Alert
                      severity="error"
                      onClose={() => {
                        clearErrors()
                      }}
                    >
                      {parseErrors(errors)[0]}
                    </Alert>
                  </Snackbar>
                )}
              </Stack>

              {children}
            </form>
          </DynamicFormYearsContext.Provider>
        </FormProvider>
      </Grid>
    </Grid>
  )
}

export default DynamicForm
